import { Quiz, Correct, Wrong, Timeout, Lose, Win } from '@/components/Popups';
import type { RequestErrorInfo, CampaignQuiz } from '@/services/api/types';
import type  { CampaignState } from '@/redux/campaign/types';
import { getCampaignData } from '@/redux/campaign/selectors';
import { getUserEntityData } from '@/redux/user/selectors';
import LocalizedImage from '@CS/components/LocalizedImage';
import { RequestError } from '@CS/components/Popups';
import { getLogged } from '@/redux/auth/selectors';
import { MS_IN_DAY } from '@CS/utils/Number';
import { useState, useEffect } from 'react';
import { useNavigateQuery } from '@/hooks';
import { shuffle } from '@CS/utils/Array';
import type { AxiosError } from 'axios';
import CSS from './Quizzes.module.css';
import { useLoader } from '@CS/hooks';
import { connect } from 'react-redux';
import Images from '@/assets/images';
import API from '@/services/api';

type UserData = {
  availableToday: number;
  correctAnswers: number;
  lastCheck: string;
  indices: number[];
};

type QuizzesProps = {
  campaign: CampaignState;
  dailyQuizzes: number;
  userData?: Object;
  logged?: boolean;
};

const Quizzes = ({ campaign, dailyQuizzes, userData, logged }: QuizzesProps) => {
  const navigate = useNavigateQuery();

  const [loading, setLoading] = useState(true);
  const loader = useLoader(loading);

  const [quizzesError, setQuizzesError] = useState<RequestErrorInfo>();
  const cancelQuizzes = () => setQuizzesError(void 0);

  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizzes, setQuizzes] = useState<CampaignQuiz[]>([]);
  const [quizIndex, setQuizIndex] = useState(-1);

  const [visibleQuiz, setVisibleQuiz] = useState(false);
  const [closeQuiz, setCloseQuiz] = useState(false);
  const hideQuiz = () => setVisibleQuiz(false);

  const [visibleCorrect, setVisibleCorrect] = useState(false);
  const hideCorrect = () => ~setVisibleCorrect(false) && showNextQuiz();

  const showCorrect = () =>
    ~setCloseQuiz(true) &&
    ~setVisibleCorrect(true) &&
    setCorrectAnswers(correctAnswers + 1);

  const [visibleWrong, setVisibleWrong] = useState(false);
  const showWrong = () => ~setCloseQuiz(true) && setVisibleWrong(true);
  const hideWrong = () => ~setVisibleWrong(false) && showNextQuiz();

  const [visibleTimeout, setVisibleTimeout] = useState(false);
  const showTimeout = () => ~setCloseQuiz(true) && setVisibleTimeout(true);
  const hideTimeout = () => ~setVisibleTimeout(false) && showNextQuiz();

  const [visibleLose, setVisibleLose] = useState(false);
  const showLose = () => setVisibleLose(true);

  const [visibleWin, setVisibleWin] = useState(false);
  const showWin = () => setVisibleWin(true);

  const setTodayQuizzes = async (quizzes: CampaignQuiz[]) => {
    let { indices, correctAnswers = 0, lastCheck: lastCall, availableToday = 0 } =
      !Object.keys(userData ?? {}).length
        ? await setUserQuizzes(quizzes)
        : userData as UserData;

    if (!availableToday) {
      const now = new Date().toISOString().slice(0, 10);
      if (lastCall === now) return navigate('/done');

      availableToday = dailyQuizzes;
      const lastCheck = new Date().toISOString().slice(0, 10);
      await API.updateUser({ data: { availableToday, lastCheck, correctAnswers: 0 }});
    }

    setCorrectAnswers(correctAnswers);
    let startDate = new Date(campaign.startTime);

    startDate = new Date(startDate.toISOString().slice(0, -1));
    const now = new Date(new Date().toISOString().slice(0, -1));

    const todayIndex = (now.getTime() - startDate.getTime()) / MS_IN_DAY | 0;
    const startIndex = todayIndex * dailyQuizzes + dailyQuizzes - availableToday;
    const endIndex = (todayIndex + 1) * dailyQuizzes;

    return setQuizzes(indices.slice(startIndex, endIndex).map(index =>
      quizzes[index]
    ));
  };

  // Shuffle quiz indices to have the least possible number
  // of repetitions throughout the duration of the campaign.
  const setUserQuizzes = async (quizzes: CampaignQuiz[]) => {
    const now = new Date(new Date().toISOString().slice(0, -1));
    let startDate = new Date(campaign.startTime);
    let endDate = new Date(campaign.endTime);

    startDate = new Date(startDate.toISOString().slice(0, -1));
    endDate = new Date(endDate.toISOString().slice(0, -1));

    if (startDate < now && now < endDate) {
      const startTime = startDate.getTime(), endTime = endDate.getTime();
      const campaignDays = Math.ceil((endTime - startTime) / MS_IN_DAY);
      const repetitions = Math.ceil(campaignDays / quizzes.length);

      const data = {
        correctAnswers: 0,
        availableToday: dailyQuizzes,
        lastCheck: new Date().toISOString().slice(0, 10),
        indices: Array.from({ length: repetitions }).reduce((i: number[]) =>
          i.concat(shuffle(quizzes.map((_, q) => q)))
        , [])
      };

      return (await API.updateUser({ data })).data as UserData;
    }

    return { correctAnswers: 0, availableToday: 0, lastCheck: now.toISOString(), indices: [] };
  };

  const onQuizError = (error: AxiosError) =>
    setQuizzesError(API.getRequestErrorInfo(error));

  const showNextQuiz = () => {
    let { availableToday = 0 } = userData as UserData;
    if (visibleWin || visibleLose) return;
    const index = quizIndex + 1;
    setCloseQuiz(false);

    if (!quizzes[index]) {
      API.updateUser({ data: { correctAnswers, availableToday }});
      return correctAnswers && ~showWin() || showLose();
    }

    setLoading(false);
    setQuizIndex(index);
    setVisibleQuiz(true);

    availableToday = Math.max(--availableToday, 0);
    API.updateUser({ data: { correctAnswers, availableToday }});
  };

  useEffect(() => {
    visibleQuiz
      ? undefined
      : quizzes.length
        ? showNextQuiz()
        : userData && logged &&
          API.getCampaignQuizzes()
            .then(({ quizzes, available }) =>
              available && !setTodayQuizzes(quizzes)
            )
            .catch(onQuizError);
  }, [userData, quizzes, logged]);

  return (
    <main className={CSS.quizzesScreen} aria-label="Quizzes">
      <header>
        <LocalizedImage name="The Good Trivia" images={Images.Logo} />
      </header>

      {loader || visibleQuiz && (
        <Quiz
          quiz={quizzes[quizIndex].quiz}
          onCorrect={showCorrect}
          onTimeout={showTimeout}
          onError={onQuizError}
          index={quizIndex + 1}
          onWrong={showWrong}
          onClose={hideQuiz}
          close={closeQuiz}
        />
      )}

      {visibleCorrect && (
        <Correct
          last={quizIndex === quizzes.length - 1}
          quiz={quizzes[quizIndex].quiz}
          onClose={hideCorrect}
        />
      )}

      {visibleWrong && (
        <Wrong
          last={quizIndex === quizzes.length - 1}
          quiz={quizzes[quizIndex].quiz}
          onClose={hideWrong}
        />
      )}

      {visibleTimeout && (
        <Timeout
          last={quizIndex === quizzes.length - 1}
          quiz={quizzes[quizIndex].quiz}
          onClose={hideTimeout}
        />
      )}

      {visibleLose && <Lose />}

      {visibleWin && <Win />}

      {quizzesError && (
        <RequestError info={quizzesError} onClose={cancelQuizzes} />
      )}
    </main>
  );
};

const mapStateToProps = (state: never) => ({
  userData: getUserEntityData(state),
  campaign: getCampaignData(state),
  logged: getLogged(state)
});

export default connect(mapStateToProps)(Quizzes);
